import { Box, Button, Card, CardContent, Chip, IconButton, TextField, Typography, useTheme, useMediaQuery } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EventIcon from '@mui/icons-material/Event';
import ListEmptyState from './ListEmptyState';

export interface TripListItem {
    id: string;
    name?: string | null;
    description?: string | null;
    events?: Array<{ id?: string; name?: string | null }>;
    /** Matches Trip detail Issues tab count; only shown in the list when positive */
    issueCount?: number;
}

export interface TripsListSectionProps {
    filteredTrips: TripListItem[];
    searchQuery: string;
    onSearchChange: (value: string) => void;
    onCreateTrip: () => void;
    onTripClick: (tripId: string) => void;
    onDeleteClick: (e: React.MouseEvent, trip: TripListItem) => void;
    createButtonLabel?: string;
}

const TOUCH_TARGET_MIN = 48;

export default function TripsListSection({
    filteredTrips,
    searchQuery,
    onSearchChange,
    onCreateTrip,
    onTripClick,
    onDeleteClick,
    createButtonLabel = 'Create trip',
}: TripsListSectionProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
            <Card sx={{ mb: 3, boxShadow: 2 }}>
                <CardContent sx={{ p: 2 }}>
                    <TextField
                        size={isMobile ? 'medium' : 'small'}
                        placeholder="Search by name"
                        value={searchQuery}
                        onChange={e => onSearchChange(e.target.value)}
                        fullWidth
                        sx={{ minWidth: isMobile ? 0 : 200 }}
                    />
                </CardContent>
            </Card>

            <Box sx={{ mb: 3 }}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={onCreateTrip}
                    fullWidth
                    sx={{ minHeight: isMobile ? TOUCH_TARGET_MIN : undefined }}
                >
                    {createButtonLabel}
                </Button>
            </Box>

            <Card sx={{ mb: 3, boxShadow: 2 }}>
                <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Trips
                    </Typography>
                    {filteredTrips.length > 0 ? (
                        <Box>
                            {filteredTrips.map(trip => {
                                const events = (trip.events ?? []) as Array<{ id?: string; name?: string | null }>;
                                return (
                                    <Box
                                        key={trip.id}
                                        sx={{
                                            py: 2,
                                            minHeight: 56,
                                            borderBottom: '1px solid',
                                            borderColor: 'divider',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            justifyContent: 'space-between',
                                            gap: 1,
                                            '&:hover': { bgcolor: 'action.hover' },
                                            '&:last-of-type': { borderBottom: 'none' },
                                        }}
                                        onClick={() => onTripClick(trip.id)}
                                    >
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                {trip.name ?? 'Unnamed trip'}
                                            </Typography>
                                            {trip.description && (
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                    {trip.description}
                                                </Typography>
                                            )}
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                                {(trip.issueCount ?? 0) > 0 && (
                                                    <Box
                                                        sx={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                            mr: 0.5,
                                                        }}
                                                    >
                                                        <ErrorOutlineIcon sx={{ fontSize: 18, color: 'error.main' }} />
                                                        <Typography component="span" variant="body2" sx={{ color: 'error.main' }}>
                                                            {trip.issueCount} {(trip.issueCount ?? 0) === 1 ? 'issue' : 'issues'}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {events.length === 0 ? (
                                                    <Chip label="No events" size="small" variant="outlined" />
                                                ) : (
                                                    events.map((e, idx) => (
                                                        <Chip
                                                            key={e.id ?? idx}
                                                            label={e.name ?? 'Unnamed'}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    ))
                                                )}
                                            </Box>
                                        </Box>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={e => onDeleteClick(e, trip)}
                                            aria-label={`Delete ${trip.name ?? 'trip'}`}
                                            sx={{ flexShrink: 0, minWidth: TOUCH_TARGET_MIN, minHeight: TOUCH_TARGET_MIN }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                );
                            })}
                        </Box>
                    ) : searchQuery.trim() ? (
                        <ListEmptyState
                            icon={<EventIcon sx={{ fontSize: 48 }} />}
                            message="No trips match your search"
                            subtext="Try a different search term"
                        />
                    ) : (
                        <ListEmptyState
                            icon={<EventIcon sx={{ fontSize: 48 }} />}
                            message="No trips yet"
                            subtext="Create a trip to group events and operations"
                            primaryAction={{ label: 'Create trip', onClick: onCreateTrip }}
                        />
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}
